import SelectField from '@shared/components/SelectField';
import TextField from '@shared/components/TextField';
import ToggleButtonGroup from '@shared/components/ToggleButtonGroup';
import { unitList } from '@shared/entity';
import { Box, Typography, Stack } from '@mui/material';
import { useWatch } from 'react-hook-form';
import { useIntl, FormattedMessage } from 'react-intl';
import { StockFormControl } from '../stock-item-form';
import { useCurrency, useFormatCurrency } from '@features/currency/currency.hooks';

type NewItemFormProps = {
  control: StockFormControl;
  isExpertPlan: boolean;
};

export const StockItemNewForm = ({ control, isExpertPlan }: NewItemFormProps) => {
  const intl = useIntl();
  const { preferredCurrency } = useCurrency();
  const { format } = useFormatCurrency();
  const itemNameWatch = useWatch({ control, name: 'itemName' });
  const packageCount = useWatch({ control, name: 'packageCount' });
  const totalPrice = useWatch({ control, name: 'totalPrice' });
  const threshold = useWatch({ control, name: 'threshold' });
  const quantity = useWatch({ control, name: 'quantity' });
  const itemType = useWatch({ control, name: 'itemType' });
  const unit = useWatch({ control, name: 'unit' });
  const pricePerPackage = totalPrice / packageCount;

  return (
    <>
      <TextField
        id="add-stock-new-item-name"
        control={control}
        fieldPath="itemName"
        label={<FormattedMessage id="stockItem.itemName" defaultMessage="Skladová položka" />}
        type="text"
        fullWidth
        required
      />

      {isExpertPlan ? (
        <Box>
          <Typography variant="caption" color="text.secondary" gutterBottom>
            <FormattedMessage id="stockItem.itemType" defaultMessage="Typ položky" />
          </Typography>
          <ToggleButtonGroup
            id="add-stock-new-item-type"
            control={control}
            fieldPath="itemType"
            options={[
              {
                value: 'MATERIAL',
                label: <FormattedMessage id="stockItem.material" defaultMessage="Materiál" />,
              },
              {
                value: 'GOODS',
                label: <FormattedMessage id="stockItem.goods" defaultMessage="Zboží" />,
              },
            ]}
            fullWidth
            size="small"
          />
        </Box>
      ) : null}

      {itemType === 'GOODS' && (
        <Stack direction="row" spacing={1}>
          <TextField
            id="add-stock-new-retail-price"
            control={control}
            fieldPath="retailPrice"
            label={
              <FormattedMessage
                id="stockItem.retailPrice"
                defaultMessage={`Prodejní cena (${preferredCurrency})`}
              />
            }
            type="number"
            fullWidth
          />
          <TextField
            control={control}
            fieldPath="brand"
            label={<FormattedMessage id="stockItem.brand" defaultMessage="Značka" />}
            type="text"
            fullWidth
          />
        </Stack>
      )}

      <div id="add-stock-new-item-unit">
        <SelectField
          items={unitList}
          label={intl.formatMessage({
            id: 'stockItem.unit',
            defaultMessage: 'Vyberte jednotku materiálu',
          })}
          control={control}
          fieldPath="unit"
          keyExtractor={u => u.name}
          labelExtractor={u => u.name}
        />
      </div>

      <Stack id="add-stock-new-item-package-count" direction="row" spacing={1}>
        <TextField
          fieldPath="packageCount"
          label={<FormattedMessage id="stockItem.packageCount" defaultMessage="Počet balení" />}
          type="number"
          required
          control={control}
        />
        <TextField
          fieldPath="quantity"
          label={
            <FormattedMessage
              id="stockItem.quantity"
              defaultMessage={`Množství v jednom balení v ${unit ?? ''}`}
            />
          }
          type="number"
          fullWidth
          required
          control={control}
        />
      </Stack>

      <TextField
        id="add-stock-new-item-total-price"
        fieldPath="totalPrice"
        label={
          <FormattedMessage
            id="stockItem.totalPrice"
            defaultMessage={`Cena za všechna balení v ${preferredCurrency}`}
          />
        }
        type="number"
        fullWidth
        control={control}
      />

      {itemNameWatch && pricePerPackage ? (
        <Stack id="add-stock-new-item-control" direction="column" spacing={0.5}>
          <Typography color="text.secondary" fontSize="0.8rem" paddingLeft="0.2rem">
            <Box component="span" color="success.main" fontWeight="bold">
              <FormattedMessage id="stockItem.checkTitle" defaultMessage="Kontrola: " />
            </Box>
            <FormattedMessage
              id="stockItem.checkCalculation"
              defaultMessage={` Za 1 balení ${itemNameWatch} zaplatíte`}
            />
            <Box component="span" color="success" fontWeight="bold">
              {` ${format(pricePerPackage)}`}
            </Box>
            <br />
            <FormattedMessage
              id="stockItem.checkQuestion"
              defaultMessage="Je tomu přibližně tak?"
            />
          </Typography>
        </Stack>
      ) : null}

      <TextField
        id="add-stock-new-item-minimum-quantity"
        fieldPath="threshold"
        required
        label={intl.formatMessage({
          id: 'stockItem.minimumPackageCount',
          defaultMessage: 'Minimální počet balení',
        })}
        type="number"
        control={control}
        fullWidth
      />
      <Typography id="stock-item-new-control" variant="body2" color="text.secondary">
        <FormattedMessage
          id="stockItem.thresholdDescription"
          defaultMessage="Upozornění se zobrazí, když počet balení klesne pod <b>{threshold} ks</b>, tedy pod <b>{minQuantity} {unit}</b>."
          values={{
            threshold,
            minQuantity: Math.round(threshold * quantity),
            unit,
            b: chunks => <strong style={{ color: '#33bd1a' }}>{chunks}</strong>,
          }}
        />
      </Typography>
    </>
  );
};
